import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, DataTable, FormField, Modal, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type CentreCoutDTO,
  type CentreCoutInput,
  type CentreCoutType,
  createCentreCout,
  deleteCentreCout,
  listCentresCout,
  updateCentreCout,
} from '@/services/parametres';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const TYPES: CentreCoutType[] = ['SIEGE', 'DIRECTION', 'COMMERCIAL', 'ADMINISTRATIF', 'PARC_MATERIEL', 'AUTRE'];

interface FormValues {
  code: string;
  libelle: string;
  type: CentreCoutType;
  budget_annuel: string;
  is_active: boolean;
}

export function CentresCoutTab() {
  const queryClient = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ['centres-cout'], queryFn: listCentresCout });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CentreCoutDTO | null>(null);

  const { register, handleSubmit, reset } = useForm<FormValues>();

  const openCreate = () => {
    setEditing(null);
    reset({ code: '', libelle: '', type: 'AUTRE', budget_annuel: '', is_active: true });
    setModalOpen(true);
  };
  const openEdit = (cc: CentreCoutDTO) => {
    setEditing(cc);
    reset({
      code: cc.code,
      libelle: cc.libelle,
      type: cc.type,
      budget_annuel: cc.budget_annuel?.toString() ?? '',
      is_active: cc.is_active,
    });
    setModalOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['centres-cout'] });

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: CentreCoutInput = {
        code: values.code,
        libelle: values.libelle,
        type: values.type,
        budget_annuel: values.budget_annuel ? Number(values.budget_annuel) : null,
        is_active: values.is_active,
      };
      return editing ? updateCentreCout(editing.id, payload) : createCentreCout(payload);
    },
    onSuccess: () => {
      invalidate();
      setModalOpen(false);
      toast.success(editing ? 'Centre de coût modifié.' : 'Centre de coût créé.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCentreCout,
    onSuccess: () => {
      invalidate();
      toast.success('Centre de coût supprimé.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<CentreCoutDTO, unknown>[] = [
    { accessorKey: 'code', header: 'Code', size: 110 },
    { accessorKey: 'libelle', header: 'Libellé' },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }) => <span className="text-xs font-medium text-content-secondary">{String(getValue()).replace('_', ' ')}</span>,
    },
    {
      accessorKey: 'budget_annuel',
      header: 'Budget annuel',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? <span className="tabular-nums">{formatDZD(v)}</span> : <span className="text-content-muted">—</span>;
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Statut',
      size: 110,
      cell: ({ getValue }) =>
        getValue() ? <StatusBadge status="done" label="Actif" /> : <StatusBadge status="pending" label="Inactif" />,
    },
    {
      id: 'actions',
      header: '',
      size: 90,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)} aria-label="Modifier">
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-status-stuck"
            onClick={() => {
              if (window.confirm(`Supprimer le centre « ${row.original.libelle} » ?`)) {
                deleteMutation.mutate(row.original.id);
              }
            }}
            aria-label="Supprimer"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate}>
          <Plus /> Nouveau centre de coût
        </Button>
      </div>

      <DataTable columns={columns} data={data} loading={isLoading} pageSize={10} exportFileName="centres-cout" />

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing ? 'Modifier le centre de coût' : 'Nouveau centre de coût'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit((v) => saveMutation.mutate(v))} loading={saveMutation.isPending}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Code">
            <Input {...register('code')} disabled={!!editing} placeholder="Auto (CC-001…)" />
          </FormField>
          <FormField label="Type">
            <select {...register('type')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace('_', ' ')}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Libellé" required className="sm:col-span-2">
            <Input {...register('libelle')} placeholder="ex : Siège Oran" />
          </FormField>
          <FormField label="Budget annuel (DZD)" hint="Optionnel">
            <Input type="number" step="1" {...register('budget_annuel')} />
          </FormField>
          <FormField label="Actif">
            <label className="flex h-10 items-center gap-2 text-sm">
              <input type="checkbox" {...register('is_active')} className="size-4" />
              Centre de coût actif
            </label>
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
